<template>
    <div class="w-100 container-chat">
        <Sidebar class="strip-content" />
        <div class="page-content align-items-center d-flex flex-column">
            <PageHeader class="container" pageName="Chats" />
            <div class="container chat-wrapper px-3">
                <div class="chat-wrapper__left">
                    <div class="chat-wrapper__left-header">
                        <p class="chat-status">
                            <strong>{{ statusString }}</strong>
                        </p>
                        <input class="form-control" type="text" v-model="searchInput" placeholder="Search ..." />
                    </div>
                    <div class="myads__subheader">
                        <ul class="subtabs-nav">
                            <li :class=" { active: activeTab}" @click="selectConversationTab(true, false, false)">
                                <a href="#">
                                    Active <span v-if="orderedActiveConversations.length">({{this.filteredOrderedActiveConversations.length}})</span>
                                </a>
                            </li>
                            <li :class=" { active: inactiveTab}" @click="selectConversationTab(false, true, false)">
                                <a href="#">
                                    Archived <span v-if="orderedInactiveConversations.length">({{this.filteredOrderedInactiveConversations.length}})</span>
                                </a>
                            </li>
                            <li :class=" { active: subscribeTab}" v-show="getConversationsToSubscribeLength"  @click="selectConversationTab(false, false, true)">
                                <a href="#">
                                    Subscribe
                                    <span v-if="getConversationsToSubscribeLength.length">({{this.getConversationsToSubscribeLength.length}})</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div class="chat-list" v-show="activeTab">
                        <div class="chat-list-scroller">
                            <template v-if="!conversationsReady">
                                <ConversationListItemSkeleton v-for="i in arrayGenerator(5)" :key="`conversation-list-item-${i}`" />
                            </template>

                            <div v-else-if="orderedActiveConversations.length">
                                <div v-for="conversation in filteredOrderedActiveConversations" :key="conversation.sid" @click="selectConversation(conversation.sid)">
                                    <ConversationListItem
                                        class="chat-user"
                                        :conversations-client="conversationsClient"
                                        :conversation="conversation"
                                        :status="true"
                                        :is-selected="selectedConversation !== undefined && conversation.sid === selectedConversation.sid"
                                        @changeState="changeState(conversation, 0)"
                                        @setConversationInfo="setActiveConversationInfo"/>
                                </div>
                            </div>

                            <p style="padding: 16px" v-else>
                                <strong>No active chats.</strong><br />
                                You will see chats here either when you message someone or when you receive a message from other users.
                            </p>
                        </div>
                    </div>
                    <div class="chat-list" v-show="inactiveTab">
                        <div class="chat-list-scroller">
                            <div v-if="orderedInactiveConversations.length">
                                <div v-for="conversation in filteredOrderedInactiveConversations" :key="conversation.sid" @click="selectConversation(conversation.sid)">
                                    <ConversationListItem
                                        class="chat-user"
                                        :conversations-client="conversationsClient"
                                        :conversation="conversation"
                                        :status="false"
                                        :is-selected="selectedConversation !== undefined && conversation.sid === selectedConversation.sid"
                                        @changeState="changeState(conversation, 1)"
                                        @setConversationInfo="setInactiveConversationInfo"/>
                                </div>
                            </div>

                            <p style="padding: 16px" v-else>
                                <strong>No inactive chats.</strong><br />
                                To move a chat here, mark it as inactive in the "Active" tab.
                            </p>
                        </div>
                    </div>

                    <div class="chat-list" v-show="subscribeTab">
                        <div class="chat-list-scroller" v-if="getConversationsToSubscribeLength">
                            <ConversationToSubscribeListItem
                                class="chat-user"
                                v-for="conversationToSubscribe in conversationsState.conversationsToSubscribe"
                                :key="conversationsState.conversationToSubscribe"
                                :conversations-client="conversationsClient"
                                :conversation="conversationToSubscribe"/>
                        </div>

                        <p style="padding: 16px" v-else>
                            <strong>No active chats.</strong><br />
                            You will see chats here either when you message someone or when you receive a message from other users.
                        </p>
                    </div>
                </div>

                <div v-if="selectedConversation" class="chat-wrapper__right" :class="{ active: chatVisible }">
                    <div class="chat-wrapper__right-conversation">
                        <ConversationUserProfile
                            :conversations-client="conversationsClient"
                            :conversation="selectedConversation"
                            @see-contact-info="seeContactInfo = !seeContactInfo"
                            :key="`conversation-profile-${selectedConversation.sid}`" />
                        <Conversation :conversations-client="conversationsClient" :selected-conversation="selectedConversation" />
                    </div>
                    <ContactInfo
                        :conversations-client="conversationsClient"
                        :conversation="selectedConversation"
                        class="contact-info-desktop"
                        @close-contact-info="seeContactInfo = false"
                        :key="`contact-info-${selectedConversation.sid}`" />
                    <ContactInfo
                        :conversations-client="conversationsClient"
                        :conversation="selectedConversation"
                        v-if="seeContactInfo"
                        class="contact-info-mobile"
                        @close-contact-info="seeContactInfo = false"
                        :key="`contact-info-${selectedConversation.sid}`" />
                </div>
                <div v-else-if="conversationsReady && $route.params.conversationSid">
                    <div class="conversation-wrapper">
                        <div class="conversation-container js-scroller">Can't load chat. You're not a participant!</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { arrayGenerator } from '@helpers/helpers';
import { Client as ConversationsClient } from '@twilio/conversations';
import _ from 'lodash';
import { mapActions, mapState } from 'vuex';

import Conversation from '@components/UserChat/Conversation.vue';
import ConversationListItem from '@components/UserChat/ConversationListItem.vue';
import ConversationListItemSkeleton from '@components/UserChat/ConversationListItemSkeleton.vue';
import ConversationToSubscribeListItem from '@components/UserChat/ConversationToSubscribeListItem.vue';
import ConversationUserProfile from '@components/UserChat/ConversationUserProfile.vue';
import PageHeader from '@components/partials/PageHeader';
import Sidebar from '@components/partials/Sidebar.vue';

const ContactInfo = () => import('@components/UserChat/partials/ContactInfo.vue');

export default {
    components: {
        Conversation,
        ConversationUserProfile,
        ConversationListItem,
        ConversationListItemSkeleton,
        ConversationToSubscribeListItem,
        ContactInfo,
        PageHeader,
        Sidebar,
    },
    data() {
        return {
            loggedIn: false,
            token: null,
            status: 'default',
            statusString: 'Loading...',
            conversationsReady: false,
            initialSubscribedConversationsCount: 0,
            conversations: [],
            orderedConversations: [],
            orderedActiveConversations: [],
            orderedInactiveConversations: [],
            selectedConversation: undefined,
            minMessageTimestamp: 0,
            newMessage: '',
            chatVisible: false,
            conversationsClient: null,
            seeContactInfo: false,
            activeTab: true,
            inactiveTab: false,
            subscribeTab: false,
            searchInput: '',
        };
    },
    computed: {
        ...mapState('user', ['user']),
        ...mapState([
            'conversationsState',
        ]),
        getConversationsToSubscribeLength() {
            return this.conversationsState.conversationsToSubscribe ? this.conversationsState.conversationsToSubscribe.length : 0;
        },
        filteredOrderedActiveConversations() {
            return this.orderedActiveConversations.filter(this.filterConversations);
        },
        filteredOrderedInactiveConversations() {
            return this.orderedInactiveConversations.filter(this.filterConversations);
        },
    },
    mounted() {
        this.hideFooter();
    },
    watch: {
        user(user) {
            if (!user) {
                return;
            }
            this.getToken();
            this.statusString = 'Fetching credentials…';
            this.loggedIn = true;
            this.filterUserAgents({ active: 'active' });
        },
        conversations: _.debounce(function (conversations) {
            if (conversations.length === this.initialSubscribedConversationsCount) {
                this.selectConversation(this.$route.params.conversationSid);
            }
        }, 500),
        $route: _.debounce(function (to, from) {
            if (to.params.conversationSid !== from.params.conversationSid) {
                this.selectConversation(to.params.conversationSid);
            }
        }, 500),
    },
    methods: {
        arrayGenerator,
        ...mapActions('user', [
            'filterUserAgents',
        ]),
        getToken() {
            let authUrl = '/api/chat/auth/user';
            if (this.$route.params.agentUuid) {
                authUrl += `/${this.$route.params.agentUuid}`;
            }
            this.axios.post(authUrl).then(({ data }) => {
                this.token = data;
                this.initConversations();
            });
        },
        changeState(conversation, state) {
            if (state) {
                this.orderedInactiveConversations = this.orderedInactiveConversations.filter((item) => item.sid !== conversation.sid);
                this.orderedActiveConversations = [...this.orderedActiveConversations, conversation].sort((a, b) => (
                    a.lastMessageTimestamp < b.lastMessageTimestamp ? 1 : -1
                ));
            } else {
                this.orderedActiveConversations = this.orderedActiveConversations.filter((item) => item.sid !== conversation.sid);
                this.orderedInactiveConversations = [...this.orderedInactiveConversations, conversation].sort((a, b) => (
                    a.lastMessageTimestamp < b.lastMessageTimestamp ? 1 : -1
                ));
            }
        },
        async initConversations() {
            /*
             * http://media.twiliocdn.com/sdk/js/conversations/releases/2.0.1/docs/classes/Client.html
             */
            this.conversationsClient = await new ConversationsClient(this.token);

            this.statusString = 'Connecting to conversations…';

            this.conversationsClient.onWithReplay('connectionStateChanged', async (state) => {
                if (state === 'connecting') {
                    this.statusString = 'Connecting to conversations…';
                    this.status = 'default';
                } else if (state === 'connected') {
                    this.status = 'success';
                    this.conversationsReady = true;
                    const chatUser = this.conversationsClient.user;
                    const c = await this.conversationsClient.getSubscribedConversations();
                    this.initialSubscribedConversationsCount = c.items.length;
                    if (chatUser.friendlyName) {
                        this.statusString = `You are chatting as ${chatUser.friendlyName}`;
                    } else {
                        this.statusString = 'You are connected.';
                    }
                } else if (state === 'disconnecting') {
                    this.statusString = 'Disconnecting from conversations…';
                    this.conversationsReady = false;
                    this.status = 'default';
                } else if (state === 'disconnected') {
                    this.statusString = 'Disconnected.';
                    this.conversationsReady = false;
                    this.status = 'warning';
                } else if (state === 'denied') {
                    this.statusString = 'Failed to connect.';
                    this.conversationsReady = false;
                    this.status = 'error';
                }

                this.conversationsClient.onWithReplay('conversationJoined', async (conversation) => {
                    if (!conversation.lastMessage) {
                        await this.wait(3);
                    }

                    const conversationExists = this.findConversation(conversation.sid);

                    if (conversationExists !== undefined) {
                        return;
                    }

                    this.conversations = [...this.conversations, conversation];

                    const c = conversation;

                    const messages = await conversation.getMessages(1, conversation.lastMessage.index);
                    const lastMessage = messages.items.shift();
                    const lastMessageTimestamp = lastMessage.state.timestamp.getTime();
                    c.lastMessageTimestamp = lastMessageTimestamp;

                    const conversationStatus = this.conversationsState.conversationsStatus
                        .find((cs) => cs.conversation_sid === c.sid);

                    if (conversationStatus && !conversationStatus.is_active) {
                        this.orderedInactiveConversations = [...this.orderedInactiveConversations, c].sort((a, b) => (
                            a.lastMessageTimestamp < b.lastMessageTimestamp ? 1 : -1
                        ));
                    } else {
                        this.orderedActiveConversations = [...this.orderedActiveConversations, c].sort((a, b) => (
                            a.lastMessageTimestamp < b.lastMessageTimestamp ? 1 : -1
                        ));
                    }
                });
                this.conversationsClient.onWithReplay('conversationLeft', (conversation) => {
                    this.conversations = [...this.conversations.filter((item) => item !== conversation)];
                });
                this.conversationsClient.onWithReplay('tokenAboutToExpire', (message) => {
                    this.getToken();
                });
                this.conversationsClient.onWithReplay('tokenExpired', () => {
                    this.getToken();
                });
            });
        },
        selectConversation(conversationSid) {
            this.selectedConversation = this.findConversation(conversationSid);
            this.chatVisible = Boolean(this.selectedConversation);
        },
        findConversation(conversationSid) {
            return this.conversations.find((item) => item.sid === conversationSid);
        },
        hideFooter() {
            document.querySelector('html').classList.add('chat');
            document.querySelector('body').classList.add('chat');
            document.querySelector('footer.footer').classList.add('d-none');
        },

        selectConversationTab(activeTab, inactiveTab, subscribeTab) {
            this.activeTab = activeTab;
            this.inactiveTab = inactiveTab;
            this.subscribeTab = subscribeTab;
        },
        setInactiveConversationInfo(conversationInfo) {
            const conversationIndex = this.orderedInactiveConversations.findIndex((conversation) => conversation.sid === conversationInfo.conversation.sid);
            this.orderedInactiveConversations[conversationIndex].users = conversationInfo.users;
        },
        setActiveConversationInfo(conversationInfo) {
            const conversationIndex = this.orderedActiveConversations.findIndex((conversation) => conversation.sid === conversationInfo.conversation.sid);
            this.orderedActiveConversations[conversationIndex].users = conversationInfo.users;
        },
        filterConversations(conversation) {
            const filteredConversation = conversation.friendlyName.toLowerCase().includes(this.searchInput.toLowerCase());
            let filteredUsers = 0;
            if (conversation.users) {
                filteredUsers = conversation.users.filter((user) => user.toLowerCase().includes(this.searchInput.toLowerCase()));
            }
            return filteredConversation || filteredUsers.length > 0;
        },
        wait(seconds) {
            return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
        },
    },
};
</script>

<style>
:root {
    --app-height: 100%;
}

html.chat,
body.chat {
    padding: 0;
    margin: 0;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
}

html.chat .sidebar {
    height: 100vh;
    height: 100dvh;
}
</style>
